import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';
import URI from 'urijs';

export default class PropertiesStore {
  loading = false;
  requestWith = [];
  requestAppend = [];
  loadedPermissions = false;

  /*
   * Note that when adding new properties to the list it has to be
   * an assignment, otherwise the properties table doesn't observe the
   * change. Another fix is to map over the properties whenever
   * passing them into the table but I thought it would be best to use
   * this fix so that it doesn't cause a frustrating bug for someone
   * in the future.
   *
   * This affects loadProperties and addProperty.
   */
  properties = [];
  selectedPropertyId = null;

  // Reference to generic user store.
  // Must contain bearerToken {string}, selectedDatabase {string}, refreshBearerToken {function}, toast {function}
  userStore = null;

  constructor(userStore) {
    this.userStore = userStore;
    makeObservable(this, {
      loading: observable,
      loadedPermissions: observable,
      properties: observable,
      selectedPropertyId: observable,
      requestWith: observable,
      requestAppend: observable,

      loadProperties: action,
      loadPropertyPermissions: action,
      addProperty: action,
      updateProperty: action,
      selectProperty: action,
      clearSelectedProperty: action,
      setRequestWith: action,
      setRequestAppend: action,

      selectedProperty: computed
    });
  }

  clearStore() {
    this.loading = false;
    this.requestWith = [];
    this.requestAppend = [];
    this.loadedPermissions = false;
    this.properties = [];
    this.selectedPropertyId = null;
  }

  /**
   * Load the list of properties from the properties api.
   *
   * @param {string} selectedDatabase
   * @param {string} bearerToken
   * @param {function} refresh
   * @param {boolean} withAllOptions
   */
  loadProperties(
    selectedDatabase,
    bearerToken,
    refresh,
    withPermissions = false
  ) {
    const currentUserId = this.userStore.user.id;
    this.loading = true;

    // Build the request url
    const uri = URI(
      `${process.env.REACT_APP_PROPERTIES_API_URL}api/properties`
    );

    const requestWith = [...this.requestWith];
    const requestAppend = [...this.requestAppend];

    if (withPermissions) {
      requestWith.push('userProperties.propertyRoles.propertyPermissions');
      requestAppend.push('user_permissions');
    }

    uri.setQuery('with[]', requestWith);
    uri.setQuery('append[]', requestAppend);

    // Send the request
    return fetch(uri.toString(), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + bearerToken,
        SeasonalDatabase: selectedDatabase
      }
    })
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) refresh();
        else throw Error(res.statusText);
      })
      .then(async (data) => {
        // The user has logged into a different account
        if (currentUserId !== this.userStore.user.id) {
          this.loading = false;
          return;
        }

        /**
         * withAllOptions needs to be set before properties so that the condition on the properties
         * page which reloads properties with all options is not triggered twice.
         */
        if (withPermissions) this.loadedPermissions = true;

        this.properties = data.sort((a, b) =>
          a.name < b.name ? -1 : a.name > b.name ? 1 : 0
        );
        if (data.length > 0) {
          // Try find the property identifier saved in local storage
          const selectedPropertyIdentifier = window.localStorage.getItem(
            'selectedPropertyIdentifier'
          );
          const selectedProperty = selectedPropertyIdentifier
            ? data.find(
                (property) => property.identifier === selectedPropertyIdentifier
              )
            : null;

          // If a property identifier saved in local storage was not found trigger a toast
          if (selectedPropertyIdentifier && !selectedProperty)
            this.userStore.toast(
              'Your selected property has been deselected!',
              'warning'
            );

          // Selected either the selected property ID from local storage or
          // the first property from the list.
          await this.selectProperty(
            selectedProperty ? selectedProperty.id : data[0].id,
            selectedDatabase,
            bearerToken,
            refresh
          );
        } else {
          this.clearSelectedProperty();
          this.loading = false;
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
        // If a property is in local storage trigger a toast since it is now deselected
        if (window.localStorage.getItem('selectedPropertyIdentifier'))
          this.userStore.toast(
            'Your selected property has been deselected!',
            'warning'
          );

        this.properties = [];
        this.clearSelectedProperty();
        this.loading = false;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  /**
   * If the property does not have all permission data loaded then
   * reload the property with all required data.
   *
   * @param {int} propertyId
   * @param {string} selectedDatabase
   * @param {string} bearerToken
   * @param {function} refresh
   * @param {boolean} selectProperty
   * @returns {Promise|null}
   */
  loadPropertyPermissions(
    propertyId,
    selectedDatabase,
    bearerToken,
    refresh,
    selectProperty = false
  ) {
    this.loading = true;
    const propertyIndex = this.properties.findIndex((p) => p.id === propertyId);
    // Don't do anything if the property was not found
    if (propertyIndex < 0) {
      this.loading = false;
      return;
    }

    // Property was found so fetch the model from the list of properties
    const property = this.properties[propertyIndex];

    // Load permissions
    if (!property.user_properties || !property.user_permissions) {
      // Build the request URL
      const uri = URI(
        `${process.env.REACT_APP_PROPERTIES_API_URL}api/properties/${propertyId}`
      );

      const requestWith = [
        ...this.requestWith,
        'userProperties.propertyRoles.propertyPermissions',
        'propertyGroups'
      ];
      const requestAppend = [...this.requestAppend, 'user_permissions'];

      uri.setQuery('with[]', requestWith);
      uri.setQuery('append[]', requestAppend);

      // Send the request
      return fetch(uri.toString(), {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + bearerToken,
          SeasonalDatabase: selectedDatabase
        }
      })
        .then((res) => {
          if (res.ok) return res.json();
          else if (res.status === 401) refresh();
          else throw Error(res.statusText);
        })
        .then((data) => {
          const newProperties = [...this.properties];
          newProperties[propertyIndex] = data;
          this.properties = newProperties;
          if (selectProperty) this.setSelectedProperty(propertyId);
        })
        .catch((err) => {
          if (process.env.NODE_ENV === 'development') console.error(err);
        })
        .finally(() => {
          runInAction(() => {
            this.loading = false;
          });
        });
    }

    // Permissions are already loaded
    else {
      if (selectProperty) this.setSelectedProperty(propertyId);
      this.loading = false;
    }
  }

  /**
   * Add a new property into the properties list.
   *
   * @param {object} property
   */
  addProperty(property) {
    const newProperties = [...this.properties];
    newProperties.push(property);
    this.properties = newProperties;
  }

  /**
   * Update a property in the properties list without
   * losing previously loaded information.
   *
   * @param {object} property
   */
  updateProperty(property) {
    const i = this.properties.findIndex((p) => p.id === property.id);
    this.properties[i] = { ...this.properties[i], ...property };
  }

  /**
   * Updated the selected property ID.
   *
   * @param {int} propertyId
   * @param {string} selectedDatabase
   * @param {string} bearerToken
   * @param {function} refresh
   * @returns {Promise|null}
   */
  selectProperty(propertyId, selectedDatabase, bearerToken, refresh) {
    return this.loadPropertyPermissions(
      propertyId,
      selectedDatabase,
      bearerToken,
      refresh,
      true
    );
  }

  /**
   * Updates the selected property ID field and local storage value to be null.
   */
  clearSelectedProperty() {
    this.selectedPropertyId = null;
    window.localStorage.removeItem('selectedPropertyId');
    window.localStorage.removeItem('selectedPropertyIdentifier');
  }

  /**
   * Updates the selected property ID field and local storage value to the supplied ID.
   *
   * Should only be used as a private method.
   *
   * @param {int} propertyId
   */
  setSelectedProperty(propertyId) {
    this.selectedPropertyId = propertyId;
    window.localStorage.setItem('selectedPropertyId', propertyId);
    window.localStorage.setItem(
      'selectedPropertyIdentifier',
      this.selectedProperty.identifier
    );
  }

  /**
   * Find the selected property object.
   *
   * @returns {object}
   */
  get selectedProperty() {
    return this.properties
      ? this.properties.find(
          (property) => property.id === this.selectedPropertyId
        )
      : null;
  }

  /**
   * Set the with[] values that will be passed with each request that fetches properties.
   *
   * @param {array} requestWith
   */
  setRequestWith(requestWith) {
    this.requestWith = requestWith;
  }

  /**
   * Set the append[] values that will be passed with each request that fetches properties.
   *
   * @param {array} requestAppend
   */
  setRequestAppend(requestAppend) {
    this.requestAppend = requestAppend;
  }
}
